import React from 'react';
import { RPDF } from './components/common/react-pdf';
import { ReportHeader } from './components/ReportHeader';
import { DayVisitsCount } from './components/table/DayVisitsCount';
import { VisitRow } from './components/table/VisitRow';
import { VisitColumns } from './components/table/VisitColumns';
import { ReportStatistics } from './components/info/ReportStatistics';
import { ReportVisitInfo } from './components/common/types';
import { ReportMiniCalendar } from './components/info/ReportMiniCalendar';
import type { CalendarType, DayOfWeek, VisitTypeDto } from '@lmt/shared-types';
import { ReportPagination } from './components/ReportPagination';
import { ReportEmptyList } from './components/ReportEmptyList';
import { groupBy } from 'lodash';
import { getFormattedDate } from '@lmt/unicorn-shared/app/components/patients/common';

interface ReportProps {
  startDate: Date;
  endDate: Date;
  visits: ReportVisitInfo[];
  calendarType: CalendarType;
  visitTypes: VisitTypeDto[];
  firstDayOfWeekIndex: DayOfWeek;
}
export const CalendarReport: React.FC<ReportProps> = (props) => {
  const { startDate, endDate, visits, calendarType, visitTypes } = props;

  const groupedVisitTypes = groupBy(visitTypes, (visitType) => visitType.id);
  const groupedByTypeVisits = groupBy(
    visits,
    (visit) => groupedVisitTypes[visit.visitTypeId][0].id,
  );
  const chartData = Object.keys(groupedByTypeVisits).map(
    (visitTypeId, index) => {
      return {
        visitType: {
          order: index,
          name: groupedVisitTypes[visitTypeId][0].value,
          color: groupedByTypeVisits[visitTypeId][0].color,
        },
        count: groupedByTypeVisits[visitTypeId].length,
      };
    },
  );

  const groupedVisits = groupBy(
    visits.sort((a, b) => a.startDate!.getTime() - b.startDate!.getTime()),
    (visit) => getFormattedDate(visit.startDate),
  );

  const Report = () => (
    <RPDF.Document>
      <RPDF.Page size="A4" style={RPDF.styles.page}>
        <ReportHeader startDate={startDate} endDate={endDate} />
        <RPDF.View style={RPDF.styles.infoContainer}>
          <ReportMiniCalendar
            date={startDate}
            calendarType={calendarType}
            firstDayOfWeekIndex={props.firstDayOfWeekIndex}
          />
          <ReportStatistics data={chartData} />
        </RPDF.View>
        {visits.length > 0 ? (
          Object.keys(groupedVisits).map((dateString) => (
            <RPDF.View key={dateString}>
              <RPDF.View wrap={false}>
                <VisitColumns />
                <DayVisitsCount
                  dateString={dateString}
                  visitsCount={groupedVisits[dateString].length}
                />
                <VisitRow
                  title={
                    groupedVisitTypes[
                      groupedVisits[dateString][0].visitTypeId
                    ][0].value
                  }
                  visit={groupedVisits[dateString][0]}
                  isDark={false}
                />
              </RPDF.View>
              {groupedVisits[dateString].slice(1).map((visit, index) => (
                <VisitRow
                  key={index}
                  title={groupedVisitTypes[visit.visitTypeId][0].value}
                  visit={visit}
                  isDark={index % 2 === 0}
                />
              ))}
            </RPDF.View>
          ))
        ) : (
          <ReportEmptyList />
        )}
        <ReportPagination />
      </RPDF.Page>
    </RPDF.Document>
  );
  return <Report />;
};
